import { useState } from 'react';
import { Create, Download, Upload } from '@mui/icons-material';
import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { translateBackend } from 'assets/i18n/config';
import { Box, Skeleton, Typography } from '../../../../1-primative';
import {
  Button,
  Dialog, DialogContent, DialogFooter, DialogTitle,
  PdfViewer, DroppableFileInput,
} from '../../../../2-component';

const FETCH_FORM_COMPLETION = gql`
  query fetchFormCompletion($formCompletionId: ObjectID!) {
    fetchFormCompletion(formCompletionId: $formCompletionId) {
      id
      state
      templateType
      signingType
      unsignedUrl
      signedUrls
      translatedName { en fr }
      translatedDescription { en fr }
    }
  }
`;

export const ViewFormDialog = ({
  formCompletionId, onClose, sign, file, setFile, signLoading,
}: {
  formCompletionId: any, onClose: () => void, sign?: () => void, file?: File, setFile: (file: File) => void, signLoading: boolean,
}) => {
  const { t } = useTranslation(['workflowCompletions', 'shared']);
  const [formCompletion, setFormCompletion] = useState<any>(null);

  const { loading } = useQuery(FETCH_FORM_COMPLETION, {
    variables: { formCompletionId },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setFormCompletion(data.fetchFormCompletion);
    },
  });

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle onClose={onClose}>{loading ? <Skeleton width={200} /> : translateBackend(formCompletion.translatedName)}</DialogTitle>
      <DialogContent>
        {loading ? <Skeleton height={500} width='100%'/> : (
          <Box>
            <Typography variant='bodyLarge'>{translateBackend(formCompletion.translatedDescription)}</Typography>
            {(formCompletion.signedUrls[0] || formCompletion.unsignedUrl) && (
              <PdfViewer pdfUrl={formCompletion.signedUrls[0] || formCompletion.unsignedUrl} fullWidth/>
            )}
            {formCompletion.signingType === 'UPLOAD' && formCompletion.state === 'UNSIGNED' && (
              <>
                { formCompletion.templateType === 'PDF' && (
                  <Typography variant='bodyLarge'>{t('workflowCompletions:uploadSignedForm')}</Typography>
                )}
                <Box mt={2}>
                  <DroppableFileInput
                    onFileChosen={(dropFile: File) => {
                      setFile(dropFile);
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      { !loading && (
        <DialogFooter>
          <Box display='flex' justifyContent='space-between' width='100%'>
            <Box>
              {formCompletion.templateType === 'PDF' && formCompletion.unsignedUrl && formCompletion.state === 'UNSIGNED' && (
                <Button
                  onClick={() => window.open(formCompletion.unsignedUrl, '_blank')}
                  label={t('shared:download')}
                  variant='tonal'
                  leadingIcon={Download}
                />
              )}
            </Box>
            {
              formCompletion.state === 'UNSIGNED' && sign && (
                formCompletion.signingType === 'UPLOAD' ? (
                  <Button onClick={sign} label={t('shared:upload')} leadingIcon={Upload} disabled={!file || signLoading} />
                ) : (
                  <Button onClick={sign} label={t('shared:sign')} leadingIcon={Create} disabled={signLoading} />
                )
              )
            }
            {
              formCompletion.state === 'SIGNED' && formCompletion.signedUrls[0] && (
                <Button onClick={() => window.open(formCompletion.signedUrls[0], '_blank')} label={t('shared:download')} leadingIcon={Download} />
              )
            }
          </Box>
        </DialogFooter>
      )}
    </Dialog>
  );
};
